import { nanoid } from 'nanoid';
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useLatest } from '../src/dirty-state/hooks';
import { debounce } from '../utils/debounce';

export function useDebounce<T>(value: T, delay: number) {
	// State and setters for debounced value
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(
		() => {
			// Update debounced value after delay
			const handler = setTimeout(() => {
				setDebouncedValue(value);
			}, delay);

			// Cancel the timeout if value changes (also on delay change or unmount)
			// This is how we prevent debounced value from updating if value is changed ...
			// .. within the delay period. Timeout gets cleared and restarted.
			return () => {
				clearTimeout(handler);
			};
		},
		[value, delay] // Only re-call effect if value or delay changes
	);

	return debouncedValue;
}

/** A function to save to the server on a debounced interval. The initial save function provided will be indefinitely used, unless deps is also provided. Changing the initial value will update the state internally but NOT fire off a save call.
 *
 * @param intervalMs the interval to call the save function @default 750
 * @param save The function that is debounced. Any dependencies should be passed as subsequent parameters.
 */
export function useDebouncedSave<T>(
	initial: T,
	save: (s: T, ...params: any[]) => void | Promise<void>,
	intervalMs?: number
): [T, (x: T, ...params: any[]) => void] {
	const [state, setState] = useState(initial);
	const match = useRef(true);
	const latestRef = useLatest(save);

	const primitiveInitial = typeof initial === 'string' || typeof initial === 'number' ? initial : JSON.stringify(initial);

	useEffect(() => {
		// This effect is specially crafted to allow controlled input to set the value back in.
		// Until we detect a matching debounced state to the initial value, we won't revert back to the initial value.
		if (!match.current && JSON.stringify(initial) === JSON.stringify(state)) match.current = true;
		else if (match.current) setState(initial);
	}, [primitiveInitial, state]);

	const updateDebounced = useCallback(
		debounce((a: T, ...params: any[]) => {
			latestRef.current(a, ...params);
		}, intervalMs ?? 750),
		[]
	);

	return [
		state,
		(x: T, ...params: any[]) => {
			setState(x);
			match.current = false;
			updateDebounced(x, ...params);
		}
	];
}

type AllPromisesType<T> = {
	pendingPromise: Promise<T> | null;
	latestPromise: {
		params: any;
		nano: any;
	} | null;
	futurePromise: {
		promise: Promise<T>;
		resolve: (param: T) => void;
	} | null;
};
/**
 * This function is like debounce, but prevents overlapping promises from executing while the first promise is still running.
 * There is a large number of promise juggling in order to ensure that while we are stacked,
 * we return a promise that will resolve the last actually executed promise.
 */
export function singlePromise<T>(save: (...params: any[]) => Promise<T>, allPromises: MutableRefObject<AllPromisesType<T>>) {
	const updateDebounced = (...params: any[]) => {
		const nano = nanoid();
		if (allPromises.current.pendingPromise) {
			let resolved = false;
			// .then will occur after a promise has finished executing. This will happen for all executions of pendingPromise (eg the stacked requests).
			// The nanoid generated is a way to capture which is the latest request; and only the one that matches will actually execute.
			const pending = allPromises.current.pendingPromise.then(x => {
				if (allPromises.current.latestPromise?.nano === nano) {
					const currentThing = allPromises.current.latestPromise;
					allPromises.current.latestPromise = null;
					const pending = (allPromises.current.pendingPromise = save(...currentThing.params));
					return pending.then(y => {
						if (allPromises.current.pendingPromise === pending) {
							allPromises.current.pendingPromise = null;
						}
						if (allPromises.current.futurePromise) {
							const r = allPromises.current.futurePromise.resolve;
							allPromises.current.futurePromise = null;
							r(y);
						}
						return y;
					});
				}

				resolved = true;
				// The futurePromise is a single promise we create here that can be resolved by the thread above, for any stacked requests
				if (!allPromises.current.futurePromise) {
					let resolve;
					const promise = new Promise<T>(r => {
						resolve = r;
					});
					allPromises.current.futurePromise = { promise, resolve };
				}

				return allPromises.current.futurePromise.promise;
			});
			if (!resolved) {
				allPromises.current.latestPromise = { params, nano };
			}
			return pending;
		}
		const pending = (allPromises.current.pendingPromise = save(...params));
		return pending.then(x => {
			if (allPromises.current.pendingPromise === pending) {
				allPromises.current.pendingPromise = null;
			}
			return x;
		});
	};
	return updateDebounced;
}

/** This is the hook implementation of singlePromise function
 * Prevents overlapping promises from executing while the first promise is still running.
 * @see {@link singlePromise} for a full function description.
 */
export function useSinglePromise<T>(save: (...params: any[]) => Promise<T>) {
	const allPromises = useRef<AllPromisesType<T>>({
		pendingPromise: null,
		latestPromise: null,
		futurePromise: null
	});
	return singlePromise(save, allPromises);
}
